import { FocusNode } from '@please/lrud'
import styled, { css } from 'styled-components'

import { SidebarProps } from '@/types/componentsPropsTypes'

export const SidebarElement = styled(FocusNode)<SidebarProps>`
  position: absolute;
  top: 30px;
  left: 34px;
  display: flex;
  flex-direction: column;
  height: calc(100% - 60px);
  padding: 16px;
  width: ${({ $isOpen }) => ($isOpen ? '274px' : '122px')};
  opacity: ${({ $isVisible }) => ($isVisible ? '1' : '0')};
  transition: opacity 0.3s;
  pointer-events: ${({ $isVisible }) => ($isVisible ? 'inherit' : 'none')};
  border: 1px solid ${(props) => props.theme.colors.sidebar.border};
  text-shadow: 0 0 2px rgba(0, 0, 0, 0.3);
  overflow: hidden;
  background: ${({ $isOpen, theme }) =>
    $isOpen
      ? `linear-gradient(
    90deg,
    ${theme.colors.sidebar.sidebarBackground.start} 0%,
    ${theme.colors.sidebar.sidebarBackground.end} 100%
  )`
      : theme.colors.sidebar.sidebarBackground.start};

  z-index: 2;
  ${(props) =>
    props.$isBlurUsed &&
    css`
      @supports (backdrop-filter: blur(10px)) or (-webkit-backdrop-filter: none)
        or (-webkit-backdrop-filter: blur(10px)) {
        background: ${props.theme.colors.sidebar.blurBackground};
        backdrop-filter: blur(10px);
        box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.1);
      }
    `}
`

export const MainMenuContainer = styled(FocusNode)<{
  $subMenu: boolean
}>`
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 1;
  width: 100%;
`

export const SubMainMenu = styled(FocusNode)<{
  $subMenu: boolean
}>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  flex: 1;
  width: 100%;
  & > div {
    display: flex;
    flex-direction: column;
  }
`

export const SubMenuPlaceholder = styled('div')<{
  $show: boolean
}>`
  padding-top: 20px;
  padding-left: 56px;
  font-weight: 500;
  font-size: 18px;
  line-height: 24px;
  width: ${({ $show }) => !$show && '0'};
  overflow: hidden;
  color: ${(props) => props.theme.colors.sidebar.text};
  opacity: 0.9;
`

export const Divider = styled.div`
  width: 100%;
  border-top: 1.5px solid rgba(0, 0, 0, 0.1);
  border-bottom: 1.5px solid rgba(255, 255, 255, 0.1);
`

export const MenuItemElem = styled(FocusNode)<{
  $isActiveMenuItem: boolean
  $isSidebarFocused: boolean
}>`
  position: relative;
  display: flex;
  height: 56px;
  min-height: 56px;
  margin: 10px 0;
  align-items: center;
  justify-content: flex-start;
  padding: 0 0 0 24px;
  border-radius: 12px;
  cursor: pointer;

  & * {
    pointer-events: none;
  }

  & > div {
    position: relative;
    display: flex;
    width: 100%;
    font-size: 22px;
    font-weight: 500;
    height: 100%;
    line-height: 100%;
    color: ${(props) => props.theme.colors.sidebar.text};
    align-items: center;
    opacity: ${({ $isActiveMenuItem }) => ($isActiveMenuItem ? '1' : '0.4')};

    img {
      display: inline-block;
      width: 40px;
      height: 40px;
      margin-right: 1rem;
      vertical-align: middle;
      object-fit: contain;
      filter: drop-shadow(0 0 2px rgba(0, 0, 0, 0.3));
      &[src=''] {
        display: none;
      }
      &.heart {
        margin-left: auto;
        width: 20px;
        height: 18px;
      }
      &.registered {
        position: absolute;
        top: 4px;
        left: 28px;
        width: 16px;
        height: 16px;
      }
    }

    span {
      display: inline-block;
      vertical-align: middle;
      white-space: nowrap;
      text-overflow: ellipsis;
      overflow: hidden;
      line-height: 1.2;
    }
  }

  &.isFocusedLeaf {
    background-color: ${({ theme }) =>
      theme.colors.sidebar.activeElem.background};

    & > div {
      opacity: ${({ $isSidebarFocused }) => ($isSidebarFocused ? '1' : '0.5')};
    }
  }

  &:last-child {
    /* border: 2px solid red; */
    margin-bottom: 0;
  }
`

export const MenuButtonBack = styled.div`
  margin: 66px 0 66px 70px;
  & > button {
    border-radius: 8px;
  }
`

export const UserCreditsWrapper = styled.div<{
  $isCentered: boolean
  $fontSizeScale: number
}>`
  display: flex;
  flex-direction: column;
  width: auto;
  align-self: center;
  font-size: ${({ $fontSizeScale }) => `${Math.ceil($fontSizeScale * 13)}px`};
  max-width: 88px;
  ${({ $isCentered }) =>
    !$isCentered &&
    css`
      margin-left: 24px;
      align-self: flex-start;
      max-width: 100%;
    `};
`

export const UserCreditsBadge = styled.div<{
  $isPromotionCredits?: boolean
}>`
  display: flex;
  align-items: center;
  line-height: 24px;
  font-weight: 700;
  height: 24px;
  padding: 0 8px;
  border-radius: 12px;
  width: 100%;
  justify-content: flex-end;
  color: ${({ theme, $isPromotionCredits }) =>
    $isPromotionCredits ? theme.colors.text : theme.colors.text};
  background: ${({ theme, $isPromotionCredits }) =>
    $isPromotionCredits ? theme.colors.badgeLiveBg : theme.colors.textDark};
  box-shadow: 0 0 0 2px ${({ theme }) => theme.colors?.sidebar.blurBackground};
  margin-bottom: 10px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;

  &:last-child {
    margin-bottom: 0;
  }

  & img {
    height: 16px;
    width: 16px;
    object-fit: contain;
    margin-left: 4px;
  }
`

export const TimeContainer = styled.div<{ $isCentered: boolean }>`
  position: relative;
  font-size: 20px;
  line-height: 1;
  font-weight: 500;
  align-self: start;
  height: 50px;
  opacity: 0.9;
  ${({ $isCentered }) =>
    $isCentered
      ? css`
          text-align: center;
          width: 100%;
        `
      : css`
          margin-left: 32px;
        `}
`

export const ButtonGroup = styled.div`
  padding: 0 0 10px;
  margin: 10px 0;
  display: flex;
  flex-direction: column;
  &:last-of-type {
    flex: 1 1 auto;
    justify-content: start;
  }
`
